import React, { useState, useEffect } from 'react';
import { ClientEntity } from '../types';
import { 
  LayoutDashboard, Calendar as CalendarIcon, CheckCircle2, 
  BarChart2, Zap, Instagram, AlertCircle, Edit3, Image as ImageIcon,
  Clock, Share2, Eye, ThumbsUp, MessageCircle, PlayCircle, Settings,
  Sparkles, Loader2, Copy, Check, ChevronLeft, ChevronRight, Video,
  BookOpen, Send, RefreshCw, FileText
} from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { toast } from 'sonner';
import { db } from '../services/db';
import { gerarJson } from '../services/geminiService';

/**
 * O prompt do pacote de conteúdo.
 *
 * Ele morava dentro de `server.ts`, na rota `/api/generate-social-content`,
 * junto de um cliente do Gemini próprio. A rota saiu (chave de terceiro não fica
 * no host do painel, e o gasto dela não aparecia em relatório nenhum); o texto,
 * que é o trabalho de verdade, veio junto e sem uma vírgula de diferença.
 */
const PROMPT_CONTEUDO_SOCIAL = (d: {
  clientName: string;
  niche: string;
  summary: string;
  differentials: string;
  products: string;
  targetAudience: string;
}) => `Você é um estrategista de conteúdo e Growth para redes sociais de alto nível especializado no mercado brasileiro.
Crie um pacote de conteúdo completo e pronto para publicação para a seguinte empresa:

Empresa: ${d.clientName || 'Empresa'}
Nicho/Segmento: ${d.niche || 'Geral'}
Resumo da Empresa: ${d.summary || ''}
Diferenciais: ${d.differentials || 'Qualidade, atendimento rápido, garantia'}
Principais Produtos/Serviços: ${d.products || 'Serviços especializados'}
Público-Alvo: ${d.targetAudience || 'Clientes locais e decisores'}

Gere exatamente:
1. 10 Posts completos para Instagram (mix entre Carrossel Educativo, Quebra de Objeção, Prova de Autoridade, Dores e Oferta Direta com CTA para o WhatsApp).
2. 5 Roteiros de Vídeo Curto (Reels/Stories) com Gancho forte (primeiros 3 segundos), Corpo/Retenção e Chamada para Ação direta.
3. Sugestão de distribuição semanal (Dias e horários recomendados).

Retorne APENAS um JSON válido, estritamente sem markdown, sem crases no início e no final, seguindo este formato exato:
{
  "posts": [
    {
      "id": "post-1",
      "format": "Carrossel" | "Feed Estático" | "Vídeo",
      "theme": "Tema do Post",
      "headline": "Título chamativo do post",
      "hook": "Gancho do primeiro slide ou primeira linha",
      "carousel_slides": ["Slide 1: ...", "Slide 2: ...", "Slide 3: ...", "Slide 4: ...", "Slide 5: Chamada para ação"],
      "caption": "Legenda completa formatada com emojis e chamada para o link da bio/WhatsApp...",
      "whatsapp_cta": "Chame no WhatsApp pelo link da bio e garanta sua condição exclusiva.",
      "image_idea": "Descrição visual recomendada para a imagem ou arte",
      "suggested_day": "Segunda-feira",
      "suggested_time": "18:30"
    }
  ],
  "video_scripts": [
    {
      "id": "script-1",
      "title": "Título do Reel",
      "hook": "Gancho verbal para os primeiros 3 segundos",
      "body": "Desenvolvimento dinâmico de 20 a 40 segundos",
      "cta": "Chamada para ação final direcionando ao WhatsApp",
      "visual_notes": "Instruções de gravação, enquadramento e legendas dinâmicas"
    }
  ],
  "strategy_summary": "Breve resumo da estratégia editorial focada em conversão"
}`;

interface PostItem {
  id: string;
  format: 'Carrossel' | 'Feed Estático' | 'Vídeo';
  theme: string;
  headline: string;
  hook: string;
  carousel_slides?: string[];
  caption: string;
  whatsapp_cta: string;
  image_idea: string;
  suggested_day: string;
  suggested_time: string;
  status?: 'planejado' | 'aprovado' | 'publicado';
  scheduledDate?: number; // Day of month (1-31)
}

interface VideoScript {
  id: string;
  title: string;
  hook: string;
  body: string;
  cta: string;
  visual_notes: string;
}

interface SocialContentState {
  posts: PostItem[];
  video_scripts: VideoScript[];
  strategy_summary: string;
  lastGenerated?: string;
}

export const SocialMediaDashboard: React.FC<{ client: ClientEntity }> = ({ client }) => {
  const [activeTab, setActiveTab] = useState<'visao' | 'posts' | 'roteiros' | 'calendario' | 'performance'>('visao');
  const [isGenerating, setIsGenerating] = useState(false);
  const [contentData, setContentData] = useState<SocialContentState | null>(null);
  const [selectedPost, setSelectedPost] = useState<PostItem | null>(null);
  const [activeSlideIndex, setActiveSlideIndex] = useState(0);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<'todos' | 'planejado' | 'aprovado' | 'publicado'>('todos');

  // Load persisted content on mount
  useEffect(() => {
    const storageKey = `nyroh_social_content_${client.id}`;
    const saved = localStorage.getItem(storageKey);
    if (saved) {
      try {
        setContentData(JSON.parse(saved));
      } catch (e) {
        console.error("Erro ao carregar conteúdo salvo:", e);
      }
    }
  }, [client.id]);

  const saveContent = (data: SocialContentState) => {
    setContentData(data);
    const storageKey = `nyroh_social_content_${client.id}`;
    localStorage.setItem(storageKey, JSON.stringify(data));
  };

  const handleGenerateContent = async () => {
    setIsGenerating(true);
    toast.info("A IA está analisando a identidade e gerando 10 posts + 5 roteiros...");

    try {
      // Fetch rich business brain if available
      let brain: any = null;
      try {
        brain = await db.getClientBusinessBrain(client.id);
      } catch (e) {
        console.log("Brain não encontrado, usando dados base.");
      }

      const clientName = client.data?.name || 'Empresa';
      const niche = (client.data?.categories && client.data.categories[0]) || 'Serviços Especializados';
      const summary = client.data?.summary || '';
      
      const differentials = brain?.differentials_data?.diferenciais?.join(', ') || 
        (client.data?.advantages || []).join(', ') || 'Atendimento rápido, profissionais qualificados e garantia';
      
      const products = brain?.products_data?.produtos?.map((p: any) => p.nome).join(', ') ||
        (client.data?.categories || []).join(', ') || 'Serviços gerais';
      
      const targetAudience = brain?.ideal_client_data?.perfil || 'Clientes locais e pessoas que buscam qualidade e confiança';

      // Pelo contrato, não pelo host Express: `/api/generate-social-content`
      // mantinha um segundo cliente do Gemini lendo `process.env.GEMINI_API_KEY`
      // direto — a chave gravada pela tela de IA não valia aqui, e este gasto
      // não aparecia no relatório de custo.
      const result = await gerarJson<any>(PROMPT_CONTEUDO_SOCIAL({
        clientName,
        niche,
        summary,
        differentials,
        products,
        targetAudience,
      }), client.id);

      // Distribute posts across days 2, 5, 8, 11, 14, 17, 20, 23, 26, 29 of the current month
      const distributedPosts = (result.posts || []).map((post: PostItem, idx: number) => ({
        ...post,
        status: (idx === 0 ? 'publicado' : idx < 4 ? 'aprovado' : 'planejado') as any,
        scheduledDate: (idx * 3) + 2
      }));

      const newContent: SocialContentState = {
        posts: distributedPosts,
        video_scripts: result.video_scripts || [],
        strategy_summary: result.strategy_summary || 'Estratégia voltada para autoridade, quebra de objeções e conversão via WhatsApp.',
        lastGenerated: new Date().toISOString()
      };

      saveContent(newContent);
      toast.success("10 Posts e 5 Roteiros gerados com sucesso!");
      setActiveTab('posts');
    } catch (err: any) {
      console.error("Erro na geração de conteúdo:", err);
      toast.error(err.message || "Erro ao gerar conteúdo.");
    } finally {
      setIsGenerating(false);
    }
  };

  const copyToClipboard = (text: string, id: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(id);
    toast.success("Copiado para a área de transferência!");
    setTimeout(() => setCopiedId(null), 2500);
  };

  const updatePostStatus = (postId: string, newStatus: 'planejado' | 'aprovado' | 'publicado') => {
    if (!contentData) return;
    const updatedPosts = contentData.posts.map(p => 
      p.id === postId ? { ...p, status: newStatus } : p
    );
    saveContent({
      ...contentData,
      posts: updatedPosts
    });
    toast.success(`Status atualizado para: ${newStatus.toUpperCase()}`);
  };

  const tabs = [
    { id: 'visao', label: 'Visão Geral', icon: LayoutDashboard },
    { id: 'posts', label: `Posts & Carrosséis (${contentData?.posts?.length || 0})`, icon: ImageIcon },
    { id: 'roteiros', label: `Roteiros de Vídeo (${contentData?.video_scripts?.length || 0})`, icon: Video },
    { id: 'calendario', label: 'Calendário Editorial', icon: CalendarIcon },
    { id: 'performance', label: 'Métricas & Benchmark', icon: BarChart2 },
  ] as const;

  const filteredPosts = (contentData?.posts || []).filter(p => {
    if (filterStatus === 'todos') return true;
    return p.status === filterStatus;
  });

  return (
    <div className="h-full flex flex-col w-full max-w-7xl mx-auto pb-12 font-sans">
      {/* Header */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-8">
        <div>
          <h2 className="text-3xl font-semibold text-gray-900 tracking-tight flex items-center gap-3">
            Conteúdo & Social Media
            {contentData && (
              <span className="text-xs bg-emerald-50 text-emerald-700 font-medium px-2.5 py-1 rounded-full border border-emerald-200">
                10 Posts Prontos
              </span>
            )}
          </h2>
          <p className="text-gray-500 mt-1">
            Fábrica de conteúdo autônoma com geração de carrosséis, copies e roteiros de conversão.
          </p>
        </div>

        <button
          onClick={handleGenerateContent}
          disabled={isGenerating}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-black hover:bg-gray-800 text-white font-medium text-sm transition-all shadow-sm disabled:opacity-50"
        >
          {isGenerating ? (
            <>
              <Loader2 size={16} className="animate-spin text-white" />
              <span>Gerando com IA...</span>
            </>
          ) : (
            <>
              <Sparkles size={16} className="text-amber-400" />
              <span>{contentData ? 'Regerar 10 Posts & Roteiros' : 'Gerar 10 Posts com IA'}</span>
            </>
          )}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex bg-white p-1 rounded-xl w-fit mb-8 shadow-sm border border-gray-200 overflow-x-auto max-w-full">
        {tabs.map(item => (
          <button 
            key={item.id}
            onClick={() => setActiveTab(item.id)}
            className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
              activeTab === item.id ? 'bg-black text-white shadow-sm' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            <item.icon size={16} className={activeTab === item.id ? 'text-white' : 'text-gray-400'} />
            {item.label}
          </button>
        ))}
      </div>

      {/* TAB CONTENT: VISÃO GERAL */}
      {activeTab === 'visao' && (
        <div className="space-y-8 animate-in fade-in duration-300">
          {/* Banner Piloto Automático */}
          <div className="bg-gradient-to-r from-gray-900 via-gray-900 to-black rounded-2xl p-6 md:p-8 text-white relative overflow-hidden border border-gray-800 shadow-md flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="relative z-10 flex items-center gap-4">
              <div className="w-12 h-12 bg-white/10 rounded-xl flex items-center justify-center border border-white/20">
                <Zap className="text-amber-400" size={24} />
              </div>
              <div>
                <h3 className="text-xl font-bold mb-1">Piloto Automático de Conteúdo</h3>
                <p className="text-gray-300 text-sm max-w-xl">
                  {contentData?.strategy_summary || 
                    "Estratégia baseada nos produtos, diferenciais e dores do cliente. Gere novos posts em lote com 1 clique."}
                </p>
              </div>
            </div>

            <div className="relative z-10 bg-white/10 backdrop-blur border border-white/10 rounded-xl p-4 flex gap-6 text-sm">
              <div>
                <p className="text-gray-400 text-xs uppercase tracking-wider font-semibold">Posts na Fila</p>
                <p className="text-white text-lg font-bold">{contentData?.posts?.length || 0} posts</p>
              </div>
              <div className="w-px bg-white/20"></div>
              <div>
                <p className="text-gray-400 text-xs uppercase tracking-wider font-semibold">Roteiros Vídeo</p>
                <p className="text-white text-lg font-bold">{contentData?.video_scripts?.length || 0} roteiros</p>
              </div>
            </div>
          </div>

          {/* Quick Metric Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { label: 'Planejados no Mês', value: contentData?.posts?.length || 0, sub: 'Frequência consistente', icon: CalendarIcon },
              { label: 'Aprovados p/ Postar', value: contentData?.posts?.filter(p => p.status === 'aprovado' || p.status === 'publicado').length || 0, sub: 'Prontos para publicação', icon: CheckCircle2 },
              { label: 'Roteiros de Reels', value: contentData?.video_scripts?.length || 0, sub: 'Vídeos curtos de alta retenção', icon: Video },
              { label: 'Direcionamento WhatsApp', value: '100%', sub: 'Todas as copies com CTA ativo', icon: MessageCircle },
            ].map((stat, idx) => (
              <div key={idx} className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{stat.label}</span>
                  <div className="p-2 bg-gray-50 rounded-lg text-gray-700">
                    <stat.icon size={18} />
                  </div>
                </div>
                <div className="text-2xl font-bold text-gray-900 mb-1">{stat.value}</div>
                <div className="text-xs text-gray-500">{stat.sub}</div>
              </div>
            ))}
          </div>

          {/* Callout if no content yet */}
          {!contentData && (
            <div className="bg-white rounded-2xl border border-dashed border-gray-300 p-12 text-center flex flex-col items-center justify-center">
              <div className="w-16 h-16 bg-amber-50 rounded-2xl flex items-center justify-center text-amber-600 mb-4">
                <Sparkles size={28} />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">Nenhum post gerado ainda</h3>
              <p className="text-gray-500 text-sm max-w-md mb-6">
                Clique no botão abaixo para a inteligência artificial analisar a empresa e gerar 10 posts com carrosséis e 5 roteiros para Reels.
              </p>
              <button
                onClick={handleGenerateContent}
                disabled={isGenerating}
                className="bg-black hover:bg-gray-800 text-white font-medium px-6 py-3 rounded-xl transition-all shadow-sm flex items-center gap-2"
              >
                {isGenerating ? <Loader2 size={18} className="animate-spin" /> : <Sparkles size={18} className="text-amber-400" />}
                Gerar 10 Posts & Roteiros com IA
              </button>
            </div>
          )}

          {/* Next Posts Preview */}
          {contentData && contentData.posts.length > 0 && (
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Próximos Posts Prontos</h3>
                  <p className="text-sm text-gray-500">Publicações organizadas para a grade editorial.</p>
                </div>
                <button 
                  onClick={() => setActiveTab('posts')}
                  className="text-xs font-semibold text-indigo-600 hover:text-indigo-800 flex items-center gap-1"
                >
                  Ver todos os 10 posts <ChevronRight size={14} />
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {contentData.posts.slice(0, 3).map((post, idx) => (
                  <div key={idx} className="border border-gray-200 rounded-xl p-4 bg-gray-50/50 flex flex-col justify-between">
                    <div>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-[11px] font-semibold bg-gray-200 text-gray-700 px-2 py-0.5 rounded">
                          {post.format}
                        </span>
                        <span className="text-xs font-medium text-gray-500">{post.suggested_day} • {post.suggested_time}</span>
                      </div>
                      <h4 className="font-bold text-sm text-gray-900 line-clamp-2 mb-2">{post.headline}</h4>
                      <p className="text-xs text-gray-600 line-clamp-3 mb-3 leading-relaxed">{post.caption}</p>
                    </div>

                    <button
                      onClick={() => {
                        setSelectedPost(post);
                        setActiveTab('posts');
                      }}
                      className="text-xs font-semibold text-black hover:underline mt-2 flex items-center gap-1"
                    >
                      Abrir Post Completo & Slides <ChevronRight size={12} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* TAB CONTENT: POSTS & CARROSSÉIS */}
      {activeTab === 'posts' && (
        <div className="space-y-6 animate-in fade-in duration-300">
          {/* Filter Bar */}
          <div className="flex flex-wrap items-center justify-between gap-4 bg-white p-4 rounded-xl border border-gray-200 shadow-sm">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-gray-500 uppercase mr-2">Filtrar por:</span>
              {(['todos', 'planejado', 'aprovado', 'publicado'] as const).map(status => (
                <button
                  key={status}
                  onClick={() => setFilterStatus(status)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-all ${
                    filterStatus === status ? 'bg-black text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {status}
                </button>
              ))}
            </div>

            <span className="text-xs text-gray-500">
              Mostrando <b>{filteredPosts.length}</b> de <b>{contentData?.posts?.length || 0}</b> publicações
            </span>
          </div>

          {!contentData || contentData.posts.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
              <p className="text-gray-500 mb-4">Nenhum post gerado.</p>
              <button
                onClick={handleGenerateContent}
                disabled={isGenerating}
                className="bg-black text-white px-5 py-2 rounded-xl text-sm font-medium"
              >
                Gerar com IA
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {filteredPosts.map((post, index) => (
                <div key={post.id || index} className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 flex flex-col justify-between hover:border-gray-300 transition-all">
                  <div>
                    {/* Top Meta */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <span className="px-2.5 py-1 rounded-md text-xs font-semibold bg-gray-900 text-white">
                          Post #{index + 1}
                        </span>
                        <span className="px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700">
                          {post.format}
                        </span>
                      </div>

                      {/* Status Selector */}
                      <select
                        value={post.status || 'planejado'}
                        onChange={(e) => updatePostStatus(post.id, e.target.value as any)}
                        className={`text-xs font-semibold rounded-lg px-2.5 py-1 border outline-none cursor-pointer ${
                          post.status === 'publicado' 
                            ? 'bg-emerald-50 text-emerald-700 border-emerald-200'
                            : post.status === 'aprovado'
                            ? 'bg-blue-50 text-blue-700 border-blue-200'
                            : 'bg-amber-50 text-amber-700 border-amber-200'
                        }`}
                      >
                        <option value="planejado">🟡 Planejado</option>
                        <option value="aprovado">🔵 Aprovado</option>
                        <option value="publicado">🟢 Publicado</option>
                      </select>
                    </div>

                    {/* Headline & Hook */}
                    <h3 className="text-lg font-bold text-gray-900 mb-1 leading-snug">{post.headline}</h3>
                    <p className="text-xs text-indigo-600 font-medium mb-4 flex items-center gap-1">
                      <Sparkles size={13} /> {post.theme}
                    </p>

                    {/* Carrossel Slide Previewer */}
                    {post.carousel_slides && post.carousel_slides.length > 0 && (
                      <div className="mb-4 bg-gray-50 border border-gray-200 rounded-xl p-4">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                            Slides do Carrossel ({post.carousel_slides.length} lâminas)
                          </span>
                          <span className="text-xs text-gray-400 font-mono">
                            {post.suggested_day} às {post.suggested_time}
                          </span>
                        </div>

                        <div className="space-y-1.5 max-h-36 overflow-y-auto pr-1">
                          {post.carousel_slides.map((slide, sIdx) => (
                            <div key={sIdx} className="text-xs text-gray-700 bg-white p-2 rounded-lg border border-gray-100 flex items-start gap-2">
                              <span className="w-5 h-5 rounded-full bg-gray-100 text-gray-600 flex items-center justify-center font-bold text-[10px] shrink-0">
                                {sIdx + 1}
                              </span>
                              <span className="flex-1">{slide}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Visual prompt idea */}
                    {post.image_idea && (
                      <div className="mb-4 p-3 bg-indigo-50/50 border border-indigo-100 rounded-xl text-xs text-indigo-900">
                        <span className="font-semibold block mb-0.5">🎨 Ideia Visual para Criação:</span>
                        {post.image_idea}
                      </div>
                    )}

                    {/* Caption Preview Box */}
                    <div className="bg-gray-50 p-4 rounded-xl text-xs text-gray-700 font-normal leading-relaxed border border-gray-200 mb-4 whitespace-pre-line max-h-40 overflow-y-auto">
                      {post.caption}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                    <button
                      onClick={() => copyToClipboard(post.caption, post.id)}
                      className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-800 transition-colors"
                    >
                      {copiedId === post.id ? <Check size={14} className="text-green-600" /> : <Copy size={14} />}
                      <span>{copiedId === post.id ? 'Copiado!' : 'Copiar Legenda Completa'}</span>
                    </button>

                    <span className="text-[11px] text-gray-400 font-medium">
                      Dia {post.scheduledDate || index + 1} do mês
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* TAB CONTENT: ROTEIROS DE VÍDEO (REELS/STORIES) */}
      {activeTab === 'roteiros' && (
        <div className="space-y-6 animate-in fade-in duration-300">
          <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-1">Roteiros de Alta Retenção (Reels / Shorts)</h3>
              <p className="text-gray-500 text-sm">
                Estruturados na fórmula: <b>Gancho 3s</b> $\rightarrow$ <b>Corpo/Dor</b> $\rightarrow$ <b>CTA para WhatsApp</b>.
              </p>
            </div>
            <span className="px-3 py-1 bg-purple-50 text-purple-700 text-xs font-bold rounded-full border border-purple-200">
              {contentData?.video_scripts?.length || 0} Roteiros Prontos
            </span>
          </div>

          {!contentData || !contentData.video_scripts || contentData.video_scripts.length === 0 ? (
            <div className="bg-white rounded-2xl border border-gray-200 p-12 text-center">
              <p className="text-gray-500 mb-4">Nenhum roteiro de vídeo disponível.</p>
              <button
                onClick={handleGenerateContent}
                disabled={isGenerating}
                className="bg-black text-white px-5 py-2 rounded-xl text-sm font-medium"
              >
                Gerar com IA
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {contentData.video_scripts.map((script, idx) => (
                <div key={script.id || idx} className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 flex flex-col justify-between hover:border-gray-300 transition-all">
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <span className="px-3 py-1 bg-black text-white text-xs font-bold rounded-lg flex items-center gap-1.5">
                        <Video size={13} /> Roteiro #{idx + 1}
                      </span>
                      <span className="text-xs text-gray-400 font-medium">Formato 9:16 (Vertical)</span>
                    </div>

                    <h4 className="text-base font-bold text-gray-900 mb-4">{script.title}</h4>

                    {/* Gancho */}
                    <div className="mb-3 bg-amber-50/70 border border-amber-200 p-3 rounded-xl">
                      <span className="text-[10px] font-bold text-amber-800 uppercase tracking-wider block mb-1">
                        🎯 Gancho Inicial (Primeiros 3 segundos):
                      </span>
                      <p className="text-xs font-semibold text-amber-950 italic">"{script.hook}"</p>
                    </div>

                    {/* Corpo */}
                    <div className="mb-3 bg-gray-50 border border-gray-200 p-3 rounded-xl">
                      <span className="text-[10px] font-bold text-gray-600 uppercase tracking-wider block mb-1">
                        🎬 Corpo do Vídeo (20-40 segundos):
                      </span>
                      <p className="text-xs text-gray-800 leading-relaxed">{script.body}</p>
                    </div>

                    {/* CTA */}
                    <div className="mb-3 bg-emerald-50/70 border border-emerald-200 p-3 rounded-xl">
                      <span className="text-[10px] font-bold text-emerald-800 uppercase tracking-wider block mb-1">
                        💬 Chamada para Ação (Final):
                      </span>
                      <p className="text-xs font-semibold text-emerald-950">"{script.cta}"</p>
                    </div>

                    {/* Visual Notes */}
                    {script.visual_notes && (
                      <div className="p-3 bg-gray-50 rounded-xl text-xs text-gray-500 border border-gray-100">
                        <span className="font-semibold text-gray-700 block mb-0.5">📌 Dica de Gravação & Edição:</span>
                        {script.visual_notes}
                      </div>
                    )}
                  </div>

                  <button
                    onClick={() => copyToClipboard(`TÍTULO: ${script.title}\n\nGANCHO: ${script.hook}\n\nCORPO:\n${script.body}\n\nCTA: ${script.cta}\n\nINSTRUÇÕES: ${script.visual_notes}`, script.id)}
                    className="mt-6 w-full flex items-center justify-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-800 text-xs font-semibold py-2.5 rounded-xl transition-colors"
                  >
                    {copiedId === script.id ? <Check size={14} className="text-green-600" /> : <Copy size={14} />}
                    <span>{copiedId === script.id ? 'Roteiro Copiado!' : 'Copiar Roteiro de Gravação'}</span>
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* TAB CONTENT: CALENDÁRIO EDITORIAL */}
      {activeTab === 'calendario' && (
        <div className="space-y-6 animate-in fade-in duration-300">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4 bg-white p-4 rounded-2xl border border-gray-200 shadow-sm">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Calendário Mensal Inteligente</h3>
              <p className="text-sm text-gray-500">Distribuição visual das postagens ao longo do mês</p>
            </div>
            <div className="flex items-center gap-4 text-xs font-medium">
              <span className="flex items-center gap-1.5 text-gray-600"><span className="w-2.5 h-2.5 rounded-full bg-amber-500"></span> Planejado</span>
              <span className="flex items-center gap-1.5 text-gray-600"><span className="w-2.5 h-2.5 rounded-full bg-blue-500"></span> Aprovado</span>
              <span className="flex items-center gap-1.5 text-gray-600"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500"></span> Publicado</span>
            </div>
          </div>

          <div className="bg-white border border-gray-200 rounded-2xl shadow-sm overflow-hidden">
            {/* Header (Dias da Semana) */}
            <div className="grid grid-cols-7 border-b border-gray-200 bg-gray-50 text-center">
              {['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'].map(dia => (
                <div key={dia} className="py-3 text-xs font-bold text-gray-500 uppercase tracking-widest">{dia}</div>
              ))}
            </div>

            {/* Grid do Calendário */}
            <div className="grid grid-cols-7 grid-rows-5 divide-x divide-y divide-gray-100 min-h-[550px]">
              {Array.from({ length: 35 }).map((_, i) => {
                const dayNumber = i + 1;
                const isCurrentMonth = dayNumber <= 31;
                const postOnThisDay = contentData?.posts?.find(p => p.scheduledDate === dayNumber);

                return (
                  <div key={i} className={`p-2 min-h-[90px] flex flex-col justify-between transition-colors ${isCurrentMonth ? 'bg-white hover:bg-gray-50/80' : 'bg-gray-50/40 text-gray-300'}`}>
                    <div className="flex justify-between items-start">
                      <span className={`text-xs font-semibold ${isCurrentMonth ? 'text-gray-700' : 'text-gray-300'}`}>
                        {isCurrentMonth ? dayNumber : ''}
                      </span>
                    </div>

                    {postOnThisDay && (
                      <div 
                        onClick={() => {
                          setSelectedPost(postOnThisDay);
                          setActiveTab('posts');
                        }}
                        className={`p-1.5 rounded-lg text-[10px] font-medium border cursor-pointer truncate transition-all hover:scale-[1.02] shadow-sm ${
                          postOnThisDay.status === 'publicado'
                            ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                            : postOnThisDay.status === 'aprovado'
                            ? 'bg-blue-50 border-blue-200 text-blue-800'
                            : 'bg-amber-50 border-amber-200 text-amber-800'
                        }`}
                        title={postOnThisDay.headline}
                      >
                        <div className="font-bold truncate">{postOnThisDay.format}</div>
                        <div className="truncate text-[9px] opacity-80">{postOnThisDay.headline}</div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* TAB CONTENT: PERFORMANCE & BENCHMARK */}
      {activeTab === 'performance' && (
        <div className="space-y-6 animate-in fade-in duration-300">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 bg-white p-6 rounded-2xl border border-gray-200 shadow-sm">
              <h3 className="text-lg font-bold text-gray-900 mb-1">Benchmark de Engajamento por Nicho</h3>
              <p className="text-sm text-gray-500 mb-6">Média esperada de conversão por linha editorial.</p>

              <div className="space-y-4">
                {[
                  { name: 'Carrossel Educativo (Problema & Solução)', rate: '4.8%', color: 'bg-emerald-500' },
                  { name: 'Vídeo Curto (Reels com Gancho)', rate: '6.2%', color: 'bg-indigo-500' },
                  { name: 'Prova Social & Depoimentos', rate: '5.1%', color: 'bg-amber-500' },
                  { name: 'Post Direto com CTA WhatsApp', rate: '7.4% (Conversão Direta)', color: 'bg-black' },
                ].map((item, idx) => (
                  <div key={idx} className="p-4 bg-gray-50 rounded-xl border border-gray-100">
                    <div className="flex justify-between items-center text-sm font-semibold text-gray-800 mb-2">
                      <span>{item.name}</span>
                      <span>{item.rate}</span>
                    </div>
                    <div className="w-full bg-gray-200 h-2 rounded-full overflow-hidden">
                      <div className={`h-full ${item.color}`} style={{ width: `${(idx + 1) * 22}%` }}></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-gray-900 text-white p-6 rounded-2xl border border-gray-800 flex flex-col justify-between">
              <div>
                <h3 className="font-semibold text-lg text-white mb-2">Diretriz da Máquina de Conteúdo</h3>
                <p className="text-sm text-gray-400 leading-relaxed mb-6">
                  "O objetivo das redes sociais neste sistema não é apenas curtidas, mas gerar tráfego qualificado para o WhatsApp e converter seguidores em clientes."
                </p>
                <div className="p-4 bg-white/10 rounded-xl text-xs text-gray-300">
                  ⚡ <b>Dica de Ouro:</b> Copie as legendas geradas e envie diretamente para publicação com os links encurtados de WhatsApp.
                </div>
              </div>

              <button
                onClick={handleGenerateContent}
                disabled={isGenerating}
                className="mt-6 w-full bg-white text-black font-semibold py-3 rounded-xl hover:bg-gray-100 transition-colors text-sm"
              >
                Gerar Novo Ciclo com IA
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
